import type {ReactNode} from 'react'
import {
  AlertIcon,
  CommentIcon,
  EyeIcon,
  GitMergeIcon,
  GitPullRequestIcon,
  PeopleIcon,
  XCircleFillIcon,
} from '@primer/octicons-react'
import {isFailingOrIncompleteStatus} from '@github-ui/mergebox/page-data/payloads/status-checks'
import type {ConflictsSectionStatuses} from '@github-ui/mergebox/sections/ConflictsSection'
import type {ConsolidatedReviewState} from '@github-ui/mergebox/sections/ReviewerSection'
import {useMergeboxStatusCalculator} from '@github-ui/mergebox/use-mergebox-status-calculator'
import type {ReviewRequestsData} from '../../../page-data/payloads/review-requests'
import {ActionItem} from './ActionItem'
import {ActionItemGroup} from './ActionItemGroup'
import type {ValidatedMergeStatusButtonData} from '../../../hooks/use-merge-status-button-data'

interface AuthorActionsProps {
  viewerLogin: string
  reviewers: ReviewRequestsData
  mergeStatusButtonData: ValidatedMergeStatusButtonData | undefined
}

interface AuthorActionsComponentProps {
  viewerLogin: string
  reviewers: ReviewRequestsData
  statusChecksData: ReturnType<typeof useMergeboxStatusCalculator>['statusChecksData']
  calculator: ReturnType<typeof useMergeboxStatusCalculator>['calculator']
  pullRequest: ReturnType<typeof useMergeboxStatusCalculator>['pullRequest']
}

/**
 * Container component for author action items.
 * This component is responsible for fetching the necessary data and calculating which action items to show for the author of the pull request.
 * The actual rendering of the action items is delegated to the AuthorActionsComponent.
 */
export function AuthorActions({viewerLogin, reviewers, mergeStatusButtonData}: AuthorActionsProps) {
  const {calculator, pullRequest, statusChecksData} = useMergeboxStatusCalculator({
    headSha: mergeStatusButtonData?.headSha ?? '',
    channels: mergeStatusButtonData?.channels ?? null,
  })

  return (
    <AuthorActionsComponent
      viewerLogin={viewerLogin}
      reviewers={reviewers}
      statusChecksData={statusChecksData}
      calculator={calculator}
      pullRequest={pullRequest}
    />
  )
}

/**
 * Presenational component for author action items.
 * We separate this from the main AuthorActions component to isolate the logic for calculating which action items to
 * show from the data fetching and state management logic in the main AuthorActions component.
 */
function AuthorActionsComponent({
  viewerLogin,
  reviewers,
  statusChecksData,
  calculator,
  pullRequest,
}: AuthorActionsComponentProps) {
  const statusChecks = statusChecksData.statusChecks
  const conflictsSection = calculator.sections.ConflictsSection
  const reviewerSection = calculator.sections.ReviewerSection

  const failingChecks = statusChecks.filter(c => isFailingOrIncompleteStatus(c.state))
  const failingRequiredChecks = failingChecks.filter(c => c.isRequired)

  const unresolvedCount = reviewers.completedReviews.reduce(
    (sum, review) => sum + (review.threadCounts.total - review.threadCounts.resolved),
    0,
  )

  const hasReviewers = reviewers.pendingReviewRequests.length > 0 || reviewers.completedReviews.length > 0
  const hasSuggestedReviewers = reviewers.suggestedReviewers.length > 0

  const items: ReactNode[] = []

  if (pullRequest.isDraft) {
    const hasOutstandingIssues = failingChecks.length > 0
    const description = hasOutstandingIssues
      ? 'Address outstanding issues before marking as ready for review.'
      : 'No outstanding issues. This pull request may be ready for review.'

    items.push(
      <ActionItem
        key="draft"
        leadingIcon={GitPullRequestIcon}
        title="Mark as ready for review"
        description={description}
        action={{onClick: () => {}, label: 'Ready for review'}}
      />,
    )
  }

  if (!hasReviewers && hasSuggestedReviewers) {
    items.push(
      <ActionItemGroup
        key="add-reviewers"
        title="Add suggested reviewers"
        count={reviewers.suggestedReviewers.length}
        items={reviewers.suggestedReviewers.map(({reviewer, description}) => ({
          leadingIcon: PeopleIcon,
          title: reviewer.displayLogin,
          description,
          action: {
            onClick: () => {
              /* TODO: wire up add reviewer */
            },
            label: 'Add reviewer',
          },
        }))}
      />,
    )
  }

  if (conflictsSection.shouldRender && hasConflicts(conflictsSection.sectionStatus)) {
    const conflictDescription = getConflictDescription(conflictsSection.sectionStatus)
    items.push(
      <ActionItem
        key="merge-conflicts"
        leadingIcon={GitMergeIcon}
        title="Merge conflicts must be resolved"
        description={conflictDescription}
        action={{onClick: () => {}, label: 'Resolve conflicts'}}
      />,
    )
  }

  if (reviewerSection.shouldRender && needsReviewerAction(reviewerSection.sectionStatus)) {
    const reviewTitle = getReviewerActionTitle(reviewerSection.sectionStatus, reviewerSection.numReviewsRequired)
    items.push(
      <ActionItem
        key="required-reviewers"
        leadingIcon={AlertIcon}
        title={reviewTitle}
        action={{onClick: () => {}, label: 'View reviewers'}}
      />,
    )
  }

  if (failingRequiredChecks.length > 0) {
    items.push(
      <ActionItemGroup
        key="failing-required-checks"
        title="Fix required failing checks"
        count={failingRequiredChecks.length}
        items={failingRequiredChecks.map(check => ({
          leadingIcon: XCircleFillIcon,
          title: check.displayName,
          description: check.description,
          action: {onClick: () => {}, label: 'Fix'},
        }))}
      />,
    )
  }

  if (unresolvedCount > 0) {
    items.push(
      <ActionItem
        key="unresolved-comments"
        leadingIcon={CommentIcon}
        title={`${unresolvedCount} unresolved comment${unresolvedCount === 1 ? '' : 's'}`}
        description="Review and resolve outstanding comments from reviewers."
        action={{onClick: () => {}, label: 'View comments'}}
      />,
    )
  }

  if (failingChecks.length > 0) {
    items.push(
      <ActionItem
        key="failing-checks"
        leadingIcon={XCircleFillIcon}
        title={`${failingChecks.length} check${failingChecks.length === 1 ? '' : 's'} failing`}
        action={{onClick: () => {}, label: 'View checks'}}
      />,
    )
  }

  if (items.length === 0) {
    items.push(
      <ActionItem
        key="no-action-items"
        leadingIcon={EyeIcon}
        title={`No action items for @${viewerLogin}`}
        description="You're all caught up!"
      />,
    )
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <ul role="list" className="d-flex flex-column gap-2">
      {items}
    </ul>
  )
}

function hasConflicts(status: ConflictsSectionStatuses): boolean {
  return status === 'HAS_CONFLICTS' || status === 'HAS_REBASE_CONFLICTS'
}

function getConflictDescription(status: ConflictsSectionStatuses): string {
  if (status === 'HAS_REBASE_CONFLICTS') {
    return 'This branch has rebase conflicts that must be resolved locally.'
  }
  return 'This branch has conflicts that must be resolved.'
}

function needsReviewerAction(status: ConsolidatedReviewState): boolean {
  return status === 'REVIEW_REQUIRED' || status === 'CHANGES_REQUESTED'
}

function getReviewerActionTitle(status: ConsolidatedReviewState, numRequired: number): string {
  if (status === 'CHANGES_REQUESTED') {
    return 'Changes requested by reviewers'
  }
  if (numRequired > 0) {
    return `${numRequired} review${numRequired === 1 ? '' : 's'} required`
  }
  return 'Review required'
}
